import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import moment from 'moment-timezone';
import { api } from 'api/api';
import { Loading } from 'components/Loading';
import Modal from 'components/Modal';
import { useTeamColors } from 'hooks';
import {
  ArticleHeader,
  ModalArticleTitle,
  GameLink,
  ArticleContent,
  ErrorMessage,
  ViewAsWebpageButton,
  ButtonContainer,
} from './style';

export const NewsModal = ({ newsId, show, onClose }) => {
  const { altColor } = useTeamColors();
  const [newsData, setNewsData] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [stadiumData, setStadiumData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processedContent, setProcessedContent] = useState('');

  const handleViewAsWebpage = () => {
    // Open standalone article in new tab
    window.open(`/news/${newsId}`, '_blank');
  };

  const handleGameLinkClick = (e) => {
    e.preventDefault();
    // Open game link in new tab
    window.open(
      `/?league=${gameInfo.league}&leagueId=${gameInfo.leagueId}&team=${encodeURIComponent(gameInfo.teamA)}&teamId=${gameInfo.teamId}&eventId=${gameInfo.eventId}`,
      '_blank'
    );
  };

  useEffect(() => {
    const fetchNewsArticle = async () => {
      try {
        setLoading(true);
        // Fetch all news and filter by news_id since API doesn't have single news endpoint
        const response = await api.news.getAllNews();

        if (response.data && response.data.length > 0) {
          // Find the specific news item by news_id
          const article = response.data.find(
            (item) => item.news_id === parseInt(newsId)
          );

          if (article) {
            // If this is a third-party URL, open in new tab
            if (article.news_url && !article.news_content) {
              window.open(article.news_url, '_blank');
              onClose(); // Close the modal after opening
              return;
            }

            // Otherwise, display the hosted content
            setNewsData(article);

            // Fetch event data if news_event_id exists
            if (article.news_event_id) {
              try {
                const eventResponse = await api.events.getEventById(
                  article.news_event_id
                );

                if (eventResponse.data) {
                  console.log(
                    'EVENT DATA FIELDS:',
                    Object.keys(eventResponse.data)
                  );
                  console.log('FULL EVENT DATA:', eventResponse.data);
                  setEventData(eventResponse.data);

                  // Fetch stadium data for timezone if event has location_id
                  if (eventResponse.data.event_location_id) {
                    try {
                      const stadiumResponse =
                        await api.games.getStadiumByLocationId(
                          eventResponse.data.event_location_id
                        );
                      if (stadiumResponse.data) {
                        setStadiumData(stadiumResponse.data);
                      }
                    } catch (stadiumErr) {
                      console.error('Error fetching stadium data:', stadiumErr);
                    }
                  }
                }
              } catch (eventErr) {
                console.error('Error fetching event data:', eventErr);
              }
            }

            // Process content to remove title and citations
            if (article.news_content) {
              let content = article.news_content;

              // Remove exact title match from content
              if (article.news_title) {
                const titleRegex = new RegExp(
                  article.news_title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                  'gi'
                );
                content = content.replace(titleRegex, '');
              }

              // Remove citation patterns like [1], [2], [1][2], etc.
              content = content.replace(/\[\d+\]/g, '');

              setProcessedContent(content);
            } else {
              setProcessedContent('');
            }
          } else {
            setError('Article not found');
          }
        } else {
          setError('No news available');
        }
      } catch (err) {
        console.error('Error fetching news article:', err);
        setError('Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    if (show && newsId) {
      fetchNewsArticle();
    }
  }, [newsId, onClose, show]);

  if (!show) return null;

  if (loading) {
    return (
      <Modal onClose={onClose} show={show}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Loading />
        </div>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal onClose={onClose} show={show}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: '20px',
          }}
        >
          <ErrorMessage $mainColor={altColor}>{error}</ErrorMessage>
        </div>
      </Modal>
    );
  }

  if (!newsData) {
    return null;
  }

  // Strip citation markers from title
  const cleanTitle =
    newsData.news_title?.replace(/\[\d+\]/g, '') || newsData.news_title;

  // Format game information: extract team names from event data
  let gameInfo = null;
  if (eventData && newsData?.news_event_id) {
    // Get main team and opponent from event API response
    const homeTeam = eventData.event_main_team || '';
    const homeTeamId = eventData.event_main_team_id;
    const opponent = eventData.event_text || '';

    // Only show link if we have both teams and event data
    if (homeTeam && opponent && eventData) {
      // Determine home or away
      const isHomeGame =
        eventData.event_location === '0' || eventData.event_location === '2';
      const vsOrAt = isHomeGame ? 'vs' : 'at';

      // Format date and time
      const eventStart = eventData.event_start;
      const timezone = stadiumData?.stadium_time_zone || 'America/Los_Angeles';

      if (eventStart) {
        const gameTime = moment.utc(eventStart).tz(timezone);
        const formattedDate = gameTime.format('ddd, MMM D, YYYY');
        const formattedTime = gameTime.format('h:mm A z');

        gameInfo = {
          teamA: homeTeam,
          teamB: opponent,
          vsOrAt,
          formattedDate,
          formattedTime,
          eventId: eventData.event_id,
          league: eventData.event_league_name,
          leagueId: eventData.event_league_id,
          teamId: homeTeamId,
        };
      }
    }
  }

  return (
    <Modal onClose={onClose} show={show}>
      <div
        style={{
          paddingTop: '40px',
          paddingLeft: '20px',
          paddingRight: '20px',
          height: '100%',
          position: 'relative',
        }}
      >
        <div
          style={{
            height: 'calc(100% - 50px)',
            overflowY: 'auto',
            paddingBottom: '5px',
          }}
        >
          <ArticleHeader $borderColor={altColor}>
            <ModalArticleTitle>{cleanTitle}</ModalArticleTitle>
            {gameInfo ? (
              <GameLink
                $color={altColor}
                href={`/?league=${gameInfo.league}&leagueId=${gameInfo.leagueId}&team=${encodeURIComponent(gameInfo.teamA)}&teamId=${gameInfo.teamId}&eventId=${gameInfo.eventId}`}
                onClick={handleGameLinkClick}
                rel="noopener noreferrer"
                target="_blank"
              >
                {gameInfo.teamA} {gameInfo.vsOrAt} {gameInfo.teamB}
                {gameInfo.formattedDate && gameInfo.formattedTime ? (
                  <>
                    {' '}
                    on {gameInfo.formattedDate} at {gameInfo.formattedTime}
                  </>
                ) : null}
              </GameLink>
            ) : null}
          </ArticleHeader>

          <ArticleContent>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {processedContent}
            </ReactMarkdown>
          </ArticleContent>
        </div>

        <ButtonContainer>
          <ViewAsWebpageButton $color={altColor} onClick={handleViewAsWebpage}>
            View as webpage
          </ViewAsWebpageButton>
        </ButtonContainer>
      </div>
    </Modal>
  );
};

NewsModal.propTypes = {
  newsId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default NewsModal;
